// Settings hub sub-navigation. Each entry maps a URL slug to a panel rendered
// inside the Settings shell (pages/settings/SettingsPage.tsx). Grouped to match
// the WBS 2.11.x structure.

import i18n from '@/i18n'

export interface SettingsNavItem {
  slug: string
  label: string
  /**
   * When true, only Admins see this item in the Settings nav.
   */
  adminOnly?: boolean
  /**
   * When true, only Admin and Manager see this item.
   */
  userManagementOnly?: boolean
}

export interface SettingsNavGroup {
  key: string
  label: string
  items: SettingsNavItem[]
}

// Settings tabs order follows the Figma settings header.
export const SETTINGS_GROUPS: SettingsNavGroup[] = [
  {
    key: 'company',
    label: i18n.t('enums:settingsTopTab.company'),
    items: [
      {
        slug: 'general-information',
        label: i18n.t('enums:settingsNavItem.general_information'),
        adminOnly: true,
      },
      {
        slug: 'users',
        label: i18n.t('enums:settingsNavItem.users'),
        userManagementOnly: true,
      },
    ],
  },
  {
    key: 'notifications',
    label: i18n.t('enums:settingsNavGroup.notifications_registry'),
    items: [
      {
        slug: 'notification-events',
        label: i18n.t('enums:settingsNavItem.notification_events'),
      },
    ],
  },
  {
    key: 'calendar',
    label: i18n.t('enums:settingsTopTab.calendar'),
    items: [
      {
        slug: 'working-days-hours',
        label: i18n.t('enums:settingsNavItem.working_days_hours'),
        adminOnly: true,
      },
      {
        slug: 'public-holidays',
        label: i18n.t('enums:settingsNavItem.public_holidays'),
        adminOnly: true,
      },
    ],
  },
  {
    key: 'services',
    label: i18n.t('enums:settingsTopTab.services'),
    items: [
      {
        slug: 'service-categories',
        label: i18n.t('enums:settingsNavItem.service_categories'),
        adminOnly: true,
      },
      {
        slug: 'service-items',
        label: i18n.t('enums:settingsNavItem.service_items'),
        adminOnly: true,
      },
      {
        slug: 'optional-fees',
        label: i18n.t('enums:settingsNavItem.optional_fees'),
        adminOnly: true,
      },
      {
        slug: 'tax-rates',
        label: i18n.t('enums:settingsNavItem.tax_rates'),
        adminOnly: true,
      },
    ],
  },
  {
    key: 'documents',
    label: i18n.t('enums:settingsTopTab.documents'),
    items: [
      {
        slug: 'document-numbering',
        label: i18n.t('enums:settingsNavItem.document_numbering'),
      },
      {
        slug: 'reusable-documents',
        label: i18n.t('enums:settingsNavItem.reusable_documents'),
        adminOnly: true,
      },
    ],
  },
  {
    key: 'templates',
    label: i18n.t('enums:settingsTopTab.document_templates'),
    items: [
      {
        slug: 'quote-template',
        label: i18n.t('enums:settingsNavItem.quote_template'),
        adminOnly: true,
      },
      {
        slug: 'invoice-template',
        label: i18n.t('enums:settingsNavItem.invoice_template'),
        adminOnly: true,
      },
      {
        slug: 'credit-note-template',
        label: i18n.t('enums:settingsNavItem.credit_note_template'),
        adminOnly: true,
      },
      {
        slug: 'contract-template',
        label: i18n.t('enums:settingsNavItem.contract_template'),
        adminOnly: true,
      },
      {
        slug: 'report-template',
        label: i18n.t('enums:settingsNavItem.report_template'),
        adminOnly: true,
      },
    ],
  },
]

export const DEFAULT_SETTINGS_SLUG = 'general-information'

/** Fallback default when Admin-only default slug is hidden for the viewer. */
export const MANAGER_DEFAULT_SETTINGS_SLUG = 'users'

export const ALL_SETTINGS_SLUGS = SETTINGS_GROUPS.flatMap(g =>
  g.items.map(i => i.slug)
)
